import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';

interface PrintRef {
  print: () => void;
}

interface SalesPrintComponentProps {
  record: any;
}

export const SalesPrintComponent = forwardRef<
  PrintRef,
  SalesPrintComponentProps
>(({ record }, ref) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const data = record;
  const CURRENCY_SYMBOL = '₹';

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Sale-${data.id || 'New'}`,
    onBeforePrint: async () => {
      // Add print-specific styles
      const style = document.createElement('style');
      style.innerHTML = `
          @media print {
            body * {
              visibility: hidden;
            }
            #sale-print, #sale-print * {
              visibility: visible;
            }
            #sale-print {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
          }
        `;
      document.head.appendChild(style);
    },
    onAfterPrint: () => {
      // Clean up print-specific styles
      const style = document.querySelector('style[data-print-style]');
      if (style) {
        style.remove();
      }
    },
  });

  useImperativeHandle(ref, () => ({
    print: handlePrint,
  }));

  // Helper function to safely format currency
  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '0';
    return parseFloat(value.toString()).toFixed(2);
  };

  // Helper function to safely format date
  const formatDate = (date: string | undefined | null) => {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY');
  };

  // Calculate subtotal (sum of mrp * quantity for all items)
  const subTotal =
    data.sale_items?.reduce((sum: number, item: any) => {
      return sum + parseFloat(item.amount);
    }, 0) || 0;

  // Calculate total item discounts (sum of discount amounts for all items)
  const totalItemDiscount =
    data.sale_items?.reduce((sum: number, item: any) => {
      const itemTotal =
        parseFloat(item.amount) * parseFloat(item.quantity || 1);
      const discountAmount = (itemTotal * parseFloat(item.discount || 0)) / 100;
      return sum + discountAmount;
    }, 0) || 0;

  // Total discount = item discounts + overall discount
  const totalDiscount =
    totalItemDiscount + parseFloat(data.discount_amount || 0);

  // Calculate subtotal after item discounts (before overall discount and GST)
  const subtotalAfterItemDiscounts = subTotal - totalItemDiscount;

  return (
    <div className="hidden print:block">
      <div
        ref={componentRef}
        id="sale-print"
        className="bg-white min-h-screen p-8"
        style={{
          paddingLeft: '3rem',
          paddingRight: '3rem',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div className="max-w-5xl mx-auto pt-[7rem] pl-10 pr-4 pb-4">
          {/* Sale Details Section */}
          <div className="grid grid-cols-2 gap-8">
            {/* Customer Information */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Customer Information
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Name:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {data.customer_name || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Phone:
                  </span>
                  <span className="text-gray-900 text-xs">
                    {data.customer_phone || 'N/A'}
                  </span>
                </div>
                {data.patient_name && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 text-xs">
                      Patient:
                    </span>
                    <span className="text-gray-900 text-xs">
                      {data.patient_name}
                    </span>
                  </div>
                )}
                {data.patient_phone && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 text-xs">
                      Patient Phone:
                    </span>
                    <span className="text-gray-900 text-xs">
                      {data.patient_phone}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Sale Details */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Sale Details
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Sale Date:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {formatDate(data.date || data.created_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Sale #:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    S-{data.id || 'NEW'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Status:
                  </span>
                  <span className="text-gray-800 text-xs font-medium">
                    {data.payment_status || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Medicine Items Table */}
          <div className="mb-4 pt-2">
            <h4 className="text-xs font-semibold text-gray-900 ">
              Medicine Items
            </h4>
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th className="px-4 py-2 text-left font-semibold text-xs text-gray-900">
                      Medicine Name
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-xs text-gray-900">
                      Pack Size
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-xs text-gray-900">
                      Qty
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-xs text-gray-900">
                      Amount
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-xs text-gray-900">
                      Discount
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-xs text-gray-900">
                      GST
                    </th>
                    <th className="px-4 py-2 text-right font-semibold text-xs text-gray-900">
                      Net Amt.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.sale_items?.map((item: any, index: number) => (
                    <tr key={index} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td className="px-4 py-1.5 text-gray-900 font-medium text-xs">
                        {item.medicine?.name || item.item_name || 'N/A'}
                      </td>
                      <td className="px-3 py-1.5 text-center text-gray-700 text-xs">
                        {item.medicine?.pack_size || 'N/A'}
                      </td>
                      <td className="px-3 py-1.5 text-center text-gray-700 text-xs">
                        {item.quantity || 1}
                      </td>
                      <td className="px-3 py-1.5 text-right text-gray-700 font-medium text-xs">
                        {CURRENCY_SYMBOL}
                        {formatCurrency(item.amount)}
                      </td>
                      <td className="px-3 py-1.5 text-center text-gray-700 text-xs">
                        {item.discount || 0}%
                      </td>
                      <td className="px-3 py-1.5 text-right text-gray-700 text-xs">
                        {item.gst_rate || 0}%
                      </td>
                      <td className="px-4 py-1.5 text-right text-gray-900 font-semibold text-xs">
                        {CURRENCY_SYMBOL}
                        {formatCurrency(item.net_amount || item.final_price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '1px solid #d1d5db' }}>
                    <td className="px-4 py-2 text-right font-bold text-xs text-gray-900"></td>
                    <td className="px-3 py-2 text-center font-bold text-xs text-gray-900"></td>
                    <td className="px-3 py-2 text-center font-bold text-xs text-gray-900"></td>
                    <td className="px-3 py-2 text-right font-bold text-xs text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(subTotal)}
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-xs text-gray-900">
                      {formatCurrency(totalItemDiscount)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-xs text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(data.tax_amount)}
                    </td>
                    <td className="px-4 py-2 text-right font-bold text-xs text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(data.bill_amount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Summary and Notes */}
          <div className="grid grid-cols-2 gap-8">
            {/* Notes Section */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Notes
              </h4>
              <div className="min-h-[90px] text-gray-700 text-xs">
                {data.notes || 'No additional notes'}
              </div>
            </div>

            {/* Payment Summary */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Payment Summary
              </h4>
              <div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-900">
                      Total Amount:
                    </span>
                    <span className="text-xs font-bold text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(
                        data.total_amount ||
                          data.final_amount ||
                          data.bill_amount
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-700 font-medium text-xs">
                    Overall Discount:
                  </span>
                  <span className="text-gray-800 font-semibold text-xs">
                    -{CURRENCY_SYMBOL}
                    {formatCurrency(data.overall_discount_amount)}
                  </span>
                </div>

                <div
                  className="flex justify-between items-center pt-3"
                  style={{ borderTop: '1px solid #d1d5db' }}
                >
                  <span className="text-gray-900 font-bold text-sm ">
                    Bill Amount:
                  </span>
                  <span className="text-gray-900 font-bold text-sm">
                    {CURRENCY_SYMBOL}
                    {formatCurrency(data.bill_amount)}
                  </span>
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-900">
                      Balance Due:
                    </span>
                    <span className="text-xs font-bold text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(data.due_amount || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="mt-4 pt-1 text-center"
            style={{ borderTop: '1px solid #e5e7eb' }}
          >
            <p className="text-gray-600 text-sm mb-2">
              Thank you for choosing Cresence Skin for your medicine needs
            </p>
            <p className="text-gray-500 text-xs">
              For any queries, please contact us at +91 74900 20099
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

SalesPrintComponent.displayName = 'SalesPrintComponent';
